import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Building2, MapPin } from 'lucide-react'
import { DashboardLayout } from '@/components/layout'
import { Card, CardContent } from '@/components/ui/card'
import { getClient } from '@/lib/api'
import { RoleRow, RolesHead } from '@/lib/rolesTable'
import { atsNav } from '@/lib/atsNav'
import { PageHeader } from '@/lib/atsUi'
import { currentTenant, currentUser } from '@/lib/mocks'

export const Route = createFileRoute('/clients/$clientId')({ component: ClientDetailPage })

const nf = (n: number) => n.toLocaleString()

function ClientDetailPage() {
  const { clientId } = Route.useParams()
  const navigate = useNavigate()
  const { data, isLoading } = useQuery({ queryKey: ['client', clientId], queryFn: () => getClient(clientId) })

  return (
    <DashboardLayout title="Client" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <Link to="/clients" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3.5 w-3.5" /> Clients
      </Link>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : !data ? (
        <div className="text-sm text-muted-foreground">Client not found.</div>
      ) : (
        <>
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-muted text-muted-foreground"><Building2 className="h-6 w-6" /></span>
            <div>
              <h1 className="text-xl font-semibold text-foreground">{data.name}</h1>
              <p className="text-sm text-muted-foreground">{data.stats.location ? <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{data.stats.location}</span> : 'Client dashboard'}</p>
            </div>
          </div>

          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Metric label="Total roles" value={nf(data.stats.totalRoles)} />
            <Metric label="Open roles" value={nf(data.stats.openRoles)} />
            <Metric label="Screened" value={nf(data.stats.screenedRoles)} />
            <Metric label="Shortlisted" value={nf(data.stats.shortlisted)} />
          </div>

          <h2 className="mb-2 text-sm font-medium">Roles</h2>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <RolesHead />
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.jobs.length === 0 ? (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-muted-foreground">No roles yet.</td></tr>
                    ) : (
                      data.jobs.map((j) => (
                        <RoleRow key={j.id} role={j} onClick={() => navigate({ to: '/jobs/$jobId', params: { jobId: j.id } })} />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </DashboardLayout>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-muted/40 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-2xl font-medium">{value}</div>
    </div>
  )
}
